import { combineReducers } from 'redux';
import _ from 'lodash';
import Immutable from 'seamless-immutable';

/**
 * Useful if you want to refactor an existing reducer and move some brnach of the state tree into another reducer
 * Example:
 *  rootState = {
 *    property1,  (Managed by root reducer)
 *    property2, (Managed by root reducer)
 *    [branchName]: { (Delegated to the branch reducer by the root reducer)
 *      property3, (Managed by branch reducer)
 *      property4, (Managed by branch reducer)
 *    }
 *  }
 * @param {function} rootReducer - the main tree reducer
 * @param {function} branchReducer - the reducer for the branch
 * @param {string} branchName - the name of the branch. This is the state node on the root state that will be managed by the branchReducer
 * @returns {function} a reducer function
 */
export function createReducerBranch(rootReducer, branchReducer, branchName) {
  return (state, action) => {
    // Safely handle the initial pass when state === undefined. Combine the initial states returned by each reducer
    if (state === undefined) {
      return rootReducer(undefined, action).set(branchName, branchReducer(undefined, action));
    }

    // Pass the branch into the branch reducer
    const branchState = branchReducer(state[branchName], action);

    // If the branch state has changed then update state, otherwise use the same state.
    const newState = state[branchName] === branchState ? state : state.set(branchName, branchState);
    return rootReducer(newState, action);
  };
}

/**
  Takes N reducers. The first reducer will get called first and then it's result will be passed
  to the next reducer.

  In the case of initial state, the last reducer will take precedence.

  You can think of the first reducer as the default with each subsequent reducer providing override behavior.
  @param {function[]} reducers - an array of reducer functions (or comma delinated ones)
  @returns {function} a single reducer function
*/
export function mergeReducers(...reducers) {
  return (state, action) => {
    return state === undefined ?
      _.reduce(reducers, (state, reducer) => state.merge(reducer(undefined, action), { deep: true }), Immutable({})) :
      _.reduce(reducers, (state, reducer) => reducer(state, action), state);
  };
}

//TODO not exporting for now. Want to do more thorough review of implementation and compare against redux combineReducers. Appears to be always returning new state and setting rather than checking for equality first.
function immutableCombineReducers(reducers) {
  const reducerKeys = Object.keys(reducers);
  return (inputState = Immutable({}), action) => {
    let newState = Immutable(inputState);

    reducerKeys.forEach(reducerName => {
      const reducer = reducers[reducerName];
      const reducerState = inputState[reducerName];
      newState = newState.set(reducerName, reducer(reducerState, action));
    });

    return newState;
  };
}

export function combineNestedReducers(reducerObj) {
  return combineReducers(
    _.reduce(
      reducerObj,
      (acc, node, key) => {
        if (_.isFunction(node)) {
          acc[key] = node;
        } else if (!_.isEmpty(node)) {
          acc[key] = combineNestedReducers(node);
        }

        return acc;
      },
      {}
    )
  );
}
